/**
 * In-memory access-token store.
 *
 * Why this file exists:
 *   The app used to keep its JWT in localStorage, which is readable by
 *   any script that achieves XSS. We now keep the short-lived access
 *   token in a plain module variable here instead. It's lost on page
 *   reload (by design), and `AuthContext` recovers a fresh one by
 *   calling `POST /api/auth/refresh`, which reads the HTTP-only refresh
 *   cookie that JavaScript can't touch.
 *
 * Why not React state alone:
 *   The token is needed in places that don't have access to React
 *   context — the axios request interceptor, the `getFetchAuthHeaders`
 *   helper used by hand-rolled `fetch` calls, and one-off service
 *   functions. A tiny module-scope store keeps the read path
 *   synchronous and doesn't force every caller through a hook.
 *
 * Listeners:
 *   `subscribe()` lets pieces of code (notably AuthContext) react when
 *   the token changes — for example, a logout caused by a 401 in the
 *   axios interceptor needs to flip React's `isAuthenticated` state.
 */

let accessToken: string | null = null;
const listeners = new Set<(token: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  if (accessToken === token) {
    return;
  }
  accessToken = token;
  listeners.forEach(listener => {
    try {
      listener(token);
    } catch {
      // Swallow listener errors so one bad subscriber can't break the
      // others. Real errors are surfaced to Sentry from inside the
      // subscriber if that subscriber chooses to.
    }
  });
}

export function clearAccessToken(): void {
  setAccessToken(null);
}

/**
 * Subscribe to access-token changes. Returns an unsubscribe function so
 * React effects can clean up. Used by AuthContext to keep its
 * `isAuthenticated` flag in sync when the token is mutated from outside
 * React (e.g. by the axios interceptor on a 401-after-refresh-fail).
 */
export function subscribeAccessToken(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
